import type { DashboardAction, DashboardDetail, DashboardStatus, TaxPaymentScope } from '../dashboardTypes';

/** What a footer control does. `link` opens the existing Tax Payment Settings page in a new tab; `refresh` re-reads the payment from the server. */
export type FooterId = 'record' | 'view' | 'void' | 'markInsufficientFunds' | 'retry' | 'resolve' | 'openTaxSettings' | 'contactNatPay' | 'refresh';

export interface FooterItem {
  id: FooterId;
  label: string;
  kind: 'button' | 'link';
  tone: 'primary' | 'secondary' | 'danger';
  enabled: boolean;
  /** Why a disabled control is disabled (the server's own reason where it gave one). Always set when `enabled` is false. */
  reason: string | null;
}

export const TAX_SETTINGS_PATH = '/tax-payment-settings';

export const READ_ONLY_REASON = 'Your plan is read only, so this change is not available until your subscription is active.';
export const PLATFORM_REASON = 'Changes to another company’s payment are not available from the all-companies view yet. Open the payment from that company.';
export const NATPAY_CONTACT_REASON = 'Not available yet: a verified NatPay support contact has not been supplied.';
export const RESOLVE_REASON = 'Resolving a payment that needs review is not available yet.';
const NOT_OFFERED = 'The server did not offer this action for this payment.';

interface Context { scope: TaxPaymentScope; readOnly: boolean }

const find = (actions: DashboardAction[], code: string) => actions.find((a) => a.code === code);

/**
 * The footer, decided from what the server says. The server's action list is the authority: an action is enabled only when the server marks it eligible, and a
 * disabled one carries the server's reason. This function only chooses which actions are RELEVANT to the status (so the footer is not a wall of disabled
 * buttons), which one is primary, and adds the plan and view limits (read-only plan, all-companies view). It never enables something the server did not.
 * Per the product owner's decisions of 2026-10-28: a primary and a secondary; Retry Payment is shown but disabled, with the reason, unless it is safe; Contact NatPay
 * Support is for the Super Admin only and stays disabled until a verified contact is supplied; Open Tax Settings opens in a new tab.
 */
export function footerFor(detail: DashboardDetail, ctx: Context): FooterItem[] {
  const { row, actions, outsideRecord } = detail;
  const status: DashboardStatus = row.status;
  const platform = ctx.scope === 'platform';

  const write = (id: FooterId, code: string, label: string, tone: FooterItem['tone']): FooterItem => {
    const action = find(actions, code);
    if (platform) return { id, label, kind: 'button', tone, enabled: false, reason: PLATFORM_REASON };
    if (ctx.readOnly) return { id, label, kind: 'button', tone, enabled: false, reason: READ_ONLY_REASON };
    if (!action) return { id, label, kind: 'button', tone, enabled: false, reason: NOT_OFFERED };
    return { id, label, kind: 'button', tone, enabled: action.eligible, reason: action.eligible ? null : (action.reason ?? NOT_OFFERED) };
  };
  const settings = (): FooterItem => ({ id: 'openTaxSettings', label: 'Open Tax Settings', kind: 'link', tone: 'secondary', enabled: true, reason: null });
  const refresh = (): FooterItem => ({ id: 'refresh', label: 'Refresh Status', kind: 'button', tone: 'secondary', enabled: true, reason: null });
  const natPayContact = (): FooterItem => ({ id: 'contactNatPay', label: 'Contact NatPay Support', kind: 'button', tone: 'secondary', enabled: false, reason: NATPAY_CONTACT_REASON });
  const view = (tone: FooterItem['tone']): FooterItem => ({ id: 'view', label: 'View Payment Record', kind: 'button', tone, enabled: true, reason: null });

  const items: FooterItem[] = [];
  const retry = write('retry', 'RetryPayment', 'Retry Payment', 'secondary');
  const record = write('record', 'RecordOutsidePayment', 'Record Outside Payment', 'secondary');
  const promote = (item: FooterItem): FooterItem => ({ ...item, tone: 'primary' });

  switch (status) {
    case 'Overdue': {
      items.push(promote(record));
      const before = row.statusBeforeOverdue;
      if (before === 'InsufficientFunds' || before === 'Failed') items.push(retry);
      if (before === 'Failed' && !platform) items.push(settings());
      if ((before === 'Failed' || before === 'NeedsReview') && platform && row.paymentPartner === 'NatPay') items.push(natPayContact());
      break;
    }
    case 'InsufficientFunds':
    case 'Failed': {
      // Retry is the primary only when the server says it is safe; otherwise it stays visible, disabled with the reason, and recording is the primary.
      items.push(retry.enabled ? promote(retry) : retry, retry.enabled ? record : promote(record));
      if (status === 'Failed' && !platform) items.push(settings());
      if (status === 'Failed' && platform && row.paymentPartner === 'NatPay') items.push(natPayContact());
      break;
    }
    case 'NeedsReview': {
      items.push({ id: 'resolve', label: 'Resolve Issue', kind: 'button', tone: 'primary', enabled: false, reason: RESOLVE_REASON });
      if (record.enabled || !platform) items.push(record);
      items.push(refresh());
      if (!platform) items.push(settings());
      if (platform && row.paymentPartner === 'NatPay') items.push(natPayContact());
      break;
    }
    case 'PendingPayment': {
      if (row.paymentPartner === 'Kotapay') items.push(write('markInsufficientFunds', 'MarkInsufficientFunds', 'Mark as Insufficient Funds', 'secondary'));
      items.push(record);
      break;
    }
    case 'PaymentProcessing': {
      if (row.paymentPartner === 'Kotapay') items.push(write('markInsufficientFunds', 'MarkInsufficientFunds', 'Mark as Insufficient Funds', 'secondary'));
      break;
    }
    case 'PaidOutsideEscochex': {
      items.push(view('primary'));
      items.push(write('void', 'VoidOutsidePaymentRecord', 'Void Outside Payment Record', 'danger'));
      break;
    }
    default:
      break;
  }

  // A voided record can still be read, whatever the status has become.
  if (outsideRecord && status !== 'PaidOutsideEscochex') items.push(view('secondary'));
  return items;
}
